/**
 * useChoreActions — claim/complete/approve/reject mutations (Plan 02-09).
 *
 * D-12 optimistic boundary contract:
 *   - claim    SERVER-AUTHORITATIVE (INV-CLAIM-2). NO onMutate. The race
 *              must be resolved by the backend; the UI shows a 200ms
 *              spinner driven by `mutation.isPending` and only flips
 *              the card after onSuccess invalidates the cache. isPending
 *              lasts until that refetch has landed (#154).
 *   - complete OPTIMISTIC. The kid presses Fullfør → card flips to
 *              `awaiting_approval` immediately → onError rolls back to
 *              the pre-mutation snapshot → onSettled refetches the
 *              canonical row.
 *   - approve  SERVER-AUTHORITATIVE. NO onMutate. invalidates ['approvals'].
 *   - reject   SERVER-AUTHORITATIVE. Body carries the reason string.
 *
 * Error surfacing (CHORE-06) — per mutation, NOT uniform (#74):
 *   - claim   toasts here. Its only callers are chore/homework cards, where a
 *             toast is the visible, correct surface:
 *               409 claim_conflict → toast(warn) with winner short_name (D-16)
 *               403 age_gate       → toast(info) with min_age (CHORE-05)
 *               500 / other        → toast(error) generic copy
 *   - approve toasts here, same reason — the button lives on an ApprovalCard.
 *   - complete does NOT toast. Same for reject. The caller owns the error
 *             surface, because both are fired from inside a <BottomSheet>:
 *             the sheet's overlay is z-50 and ToastViewport is z-40, so a
 *             toast raised from behind the scrim is invisible — and the sheet
 *             only closes onSuccess, so it stays parked over the explanation.
 *             Consumers render an inline `role="alert"` line instead
 *             (ChoreCompleteSheet, RejectReasonPanel). The one non-sheet
 *             caller of complete, HomeworkRoute, passes its own per-call
 *             onError toast. The errors are re-thrown either way, so a
 *             per-call onError sees them.
 *
 * The mutation hook reads `i18next.t(...)` for every Norwegian string —
 * MED-7 jsx-no-literals stays green.
 *
 * Takes no viewer id: every cache key it touches (['chores'], ['chores','board'],
 * ['homework'], ['approvals']) is family-wide. It used to accept one for the
 * per-viewer ['my-chores', id] key, which the unified board feed replaced.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from '../../lib/toast';
import { fireClaimConflictToast } from './ClaimConflictToast';
import type { PoolChoreInstance } from './useChoresQuery';

/**
 * Hvor lenge «Tar …» høyst venter på at tavla er hentet på nytt etter et
 * vellykket claim (#154). Vanligvis er hentingen ferdig på et par hundre ms.
 */
const REFETCH_WAIT_MS = 5_000;

/** Query key of the unified board feed (see useChoresQuery.useBoard). */
const BOARD_KEY = ['chores', 'board'] as const;

interface FetchError extends Error {
  status: number;
  body?: unknown;
}

async function postJson<T>(url: string, body?: unknown): Promise<T> {
  const init: RequestInit = {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  const r = await globalThis.fetch(url, init);
  if (!r.ok) {
    let parsed: unknown;
    try {
      parsed = await r.json();
    } catch {
      parsed = undefined;
    }
    const err = new Error(`fetch_failed:${r.status}`) as FetchError;
    err.status = r.status;
    err.body = parsed;
    throw err;
  }
  // Some endpoints (reject) may still return JSON; wrap defensively.
  try {
    return (await r.json()) as T;
  } catch {
    return undefined as T;
  }
}

/**
 * Try to read the claimer's short_name from the 409 body's `current` row.
 * The server attaches `claimed_by_short_name` when known; older rows or
 * thin payloads fall back to a localized "noen andre" copy.
 */
function readWinnerName(body: unknown, fallback: string): string {
  if (body && typeof body === 'object' && body !== null) {
    const cur = (body as { current?: { claimed_by_short_name?: unknown } }).current;
    const sn = cur?.claimed_by_short_name;
    if (typeof sn === 'string' && sn.length > 0) return sn;
  }
  return fallback;
}

function readMinAge(body: unknown): number | null {
  if (body && typeof body === 'object' && body !== null) {
    const det = (body as { details?: { min_age?: unknown } }).details;
    if (det && typeof det.min_age === 'number') return det.min_age;
  }
  return null;
}

export interface ChoreActions {
  claim: ReturnType<typeof useMutation<PoolChoreInstance, FetchError, string>>;
  complete: ReturnType<
    typeof useMutation<
      PoolChoreInstance,
      FetchError,
      { instanceId: string; proofUuid?: string },
      { prev: PoolChoreInstance[] | undefined }
    >
  >;
  approve: ReturnType<typeof useMutation<PoolChoreInstance, FetchError, string>>;
  reject: ReturnType<
    typeof useMutation<PoolChoreInstance, FetchError, { instanceId: string; reason: string }>
  >;
}

export function useChoreActions(): ChoreActions {
  const qc = useQueryClient();
  const { t } = useTranslation();

  // ── claim — server-authoritative (INV-CLAIM-2) ───────────────────────────
  // No onMutate. The mutation awaits the backend; on 200 we invalidate so
  // the next render reflects the canonical state. On 409 / 403 / 500 we
  // surface a Norwegian toast.
  const claim = useMutation<PoolChoreInstance, FetchError, string>({
    mutationFn: (instanceId) => postJson<PoolChoreInstance>(`/api/chores/${instanceId}/claim`),
    onError: (err) => {
      if (err.status === 409) {
        const winner = readWinnerName(err.body, t('chores.claim.fallbackWinner'));
        // Same kind, same body — routed through the wrapper so the rendered
        // toast carries the `claim-conflict-toast` testid (issue #56).
        fireClaimConflictToast({ t, winner });
      } else if (err.status === 403) {
        const minAge = readMinAge(err.body) ?? 0;
        toast({ kind: 'info', body: t('chores.claim.ageGate', { minAge }) });
      } else {
        toast({ kind: 'error', body: t('chores.claim.error') });
      }
    },
    // Returnerer løftet, så mutasjonen står som pending til tavla er hentet på
    // nytt (#154). Uten det slutter spinneren når serveren svarer, men kortet
    // viser «Ta» igjen fram til den nye tavla kommer, og så «Fullfør». Med det
    // går knappen rett fra «Tar …» til «Fullfør» — fortsatt uten optimisme:
    // «Fullfør» kommer fra serverens data. invalidateQueries kaster ikke, så en
    // mislykket henting avslutter bare ventingen, og en henting som står på
    // vent (nettet borte rett etter svaret) holder den høyst REFETCH_WAIT_MS.
    onSuccess: () =>
      Promise.race([
        // ['chores'] prefix-matches ['chores','board'].
        Promise.all([
          qc.invalidateQueries({ queryKey: ['chores'] }),
          qc.invalidateQueries({ queryKey: ['homework'] }),
        ]),
        new Promise<void>((resolve) => setTimeout(resolve, REFETCH_WAIT_MS)),
      ]),
  });

  // ── complete — optimistic; kid sees flip immediately ─────────────────────
  const complete = useMutation<
    PoolChoreInstance,
    FetchError,
    { instanceId: string; proofUuid?: string },
    { prev: PoolChoreInstance[] | undefined }
  >({
    mutationFn: ({ instanceId, proofUuid }) => {
      const body = proofUuid ? { proofUuid } : undefined;
      return postJson<PoolChoreInstance>(`/api/chores/${instanceId}/complete`, body);
    },
    onMutate: async ({ instanceId }) => {
      // Optimism lands on the board feed — the surface that renders the card.
      // It used to target ['my-chores'], which the board stopped reading when
      // the unified feed replaced it, silently making the flip a no-op.
      await qc.cancelQueries({ queryKey: BOARD_KEY });
      const prev = qc.getQueryData<PoolChoreInstance[]>(BOARD_KEY);
      qc.setQueryData<PoolChoreInstance[]>(BOARD_KEY, (old) =>
        old?.map((c) =>
          c.id === instanceId ? { ...c, status: 'awaiting_approval' as const } : c,
        ),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      // Rollback only — no toast (#74). See the contract at the top of the file.
      if (ctx?.prev !== undefined) {
        qc.setQueryData<PoolChoreInstance[]>(BOARD_KEY, ctx.prev);
      }
    },
    onSettled: () => {
      // ['chores'] prefix-matches ['chores','board'] too.
      void qc.invalidateQueries({ queryKey: ['chores'] });
      void qc.invalidateQueries({ queryKey: ['homework'] });
    },
  });

  // ── approve — server-authoritative ───────────────────────────────────────
  const approve = useMutation<PoolChoreInstance, FetchError, string>({
    mutationFn: (instanceId) =>
      postJson<PoolChoreInstance>(`/api/chores/${instanceId}/approve`),
    onError: () => {
      toast({ kind: 'error', body: t('approvals.error') });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      void qc.invalidateQueries({ queryKey: ['chores'] });
      void qc.invalidateQueries({ queryKey: ['homework'] });
    },
  });

  // ── reject — server-authoritative ────────────────────────────────────────
  const reject = useMutation<
    PoolChoreInstance,
    FetchError,
    { instanceId: string; reason: string }
  >({
    mutationFn: ({ instanceId, reason }) =>
      postJson<PoolChoreInstance>(`/api/chores/${instanceId}/reject`, { reason }),
    // No onError: the only caller is ApprovalCard, which shows the failure
    // inline in its RejectReasonPanel where the adult pressed (#74; the panel
    // replaced the RejectReasonSheet <BottomSheet> in #155).
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      void qc.invalidateQueries({ queryKey: ['chores'] });
      void qc.invalidateQueries({ queryKey: ['homework'] });
    },
  });

  return { claim, complete, approve, reject };
}
